import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { TimeframeValue } from "@volca/ui/components/ui/time-frame-filter";
import { useBreakpoint } from "@volca/ui/hooks/use-breakpoint";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { useViewPreferences } from "@/core/view-preferences/view-preferences-context";
import AddAdvocateModal from "@/customer/common/add-advocate-modal";
import { useGetMyUser } from "@/user/query";
import AddReferralModal from "./common/add-referral-modal";
import { PayoutModal } from "./common/referral-payout-modal";
import { REFERRAL_COLUMN_TO_STATUS } from "./constants";
import { NoReferrals } from "./no-referrals";
import { useGetActiveReferrals } from "./query";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
} from "./referral";
import ReferralKanbanView from "./referral-kanban-view";
import { UpdateReferralInfoModal } from "./referral-kanban-view/update-referral-info-modal";
import ReferralTableView from "./referral-table-view";
import { ReferralsFilterEmptyState } from "./referrals-filter-empty-state";
import { ReferralsHeader } from "./referrals-header";
import { ReferralsLoadError } from "./referrals-load-error";
import { ReferralsSidebar } from "./referrals-sidebar";
import { useFilteredReferrals } from "./use-filtered-referrals";
import { useReferralActions } from "./use-referral-actions";

export interface UpdateInfoModal {
  referral: IReferral | null;
  targetColumn: ReferralColumn | null;
}

const Referrals = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selected = searchParams.get("selected");
  const { referralView, setReferralView } = useViewPreferences();
  const { businessId } = useBusiness();
  const { data: user } = useGetMyUser();
  const isDesktop = useBreakpoint() === "desktop";

  const { data: businessDetails } = useGetBusiness(businessId);
  const {
    data: referrals = [],
    isPending: isLoadingReferrals,
    isError: isReferralsError,
    isFetching: isRefetchingReferrals,
    errorUpdatedAt,
    dataUpdatedAt,
    refetch: refetchReferrals,
  } = useGetActiveReferrals(businessId);

  const [searchValue, setSearchValue] = useState("");
  const [timeframeValue, setTimeframeValue] =
    useState<TimeframeValue>("all_time");
  const [payoutReferral, setPayoutReferral] = useState<IReferral | null>(null);
  const [isAddReferralModalOpen, setIsAddReferralModalOpen] = useState(false);
  const [isAddAdvocateModalOpen, setIsAddAdvocateModalOpen] = useState(false);
  const [updateInfoModal, setUpdateInfoModal] =
    useState<UpdateInfoModal | null>(null);

  const isServiceTitanEnabled = businessDetails?.service_titan_enabled || false;
  const effectiveView =
    referralView || (isServiceTitanEnabled ? "table" : "kanban");

  // Filter referrals based on search and timeframe
  const filteredReferrals = useFilteredReferrals(
    referrals,
    searchValue,
    timeframeValue,
  );

  // Get the selected referral from the query param
  const selectedReferral = useMemo(
    () => (selected ? referrals.find((r) => r.id === selected) || null : null),
    [selected, referrals],
  );

  const {
    handleSidebarClose,
    handleReferralUpdate,
    handleUpdateReferral,
    handleDeleteReferral,
  } = useReferralActions(businessId, selectedReferral);

  const handleUpdateDraggedReferral = useCallback(
    (values: Partial<ReferralUpdateParams>) => {
      if (!updateInfoModal?.referral || !updateInfoModal?.targetColumn) return;
      const updates = {
        status: REFERRAL_COLUMN_TO_STATUS[updateInfoModal.targetColumn],
        ...values,
      };
      handleReferralUpdate({
        referral: updateInfoModal.referral,
        updates,
        method: "modal",
        onSuccessCallback: () => setUpdateInfoModal(null),
      });
    },
    [updateInfoModal, handleReferralUpdate],
  );

  const handleSidebarUpdate = useCallback(
    (values: Partial<ReferralUpdateParams>) => {
      if (!selectedReferral) return;
      handleUpdateReferral(values, selectedReferral);
    },
    [selectedReferral, handleUpdateReferral],
  );

  const handleFinalPayout = useCallback(
    (referralId: string) => {
      const currentSelected = searchParams.get("selected");
      if (currentSelected === referralId) {
        handleSidebarClose();
      }
    },
    [searchParams, handleSidebarClose],
  );

  if (!user) return null;

  const failedLoad =
    isReferralsError ||
    (isRefetchingReferrals && errorUpdatedAt > dataUpdatedAt);
  const noReferrals = !referrals.length && !isLoadingReferrals && !failedLoad;
  const isWorkspace = isDesktop && !noReferrals;

  return (
    <SimpleSidebarLayout
      open={!!selectedReferral}
      onOpenChange={(open) => !open && handleSidebarClose()}
      data-cy="referrals-view"
    >
      <SimpleSidebarContent removeScrollArea={isWorkspace}>
        <PageContainer variant={isWorkspace ? "workspace" : "document"}>
          <ReferralsHeader
            referralsCount={filteredReferrals.length}
            isLoadingReferrals={isLoadingReferrals}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            timeframeValue={timeframeValue}
            onTimeframeChange={setTimeframeValue}
            viewType={effectiveView}
            onViewChange={(view) => setReferralView(view)}
            onAddAdvocate={() => setIsAddAdvocateModalOpen(true)}
            onAddReferral={() => setIsAddReferralModalOpen(true)}
            noReferrals={noReferrals}
          />

          {failedLoad && referrals.length === 0 ? (
            <ReferralsLoadError
              retrying={isRefetchingReferrals}
              onRetry={() => void refetchReferrals()}
            />
          ) : !isLoadingReferrals &&
            referrals.length > 0 &&
            filteredReferrals.length === 0 ? (
            <ReferralsFilterEmptyState
              onClear={() => {
                setSearchValue("");
                setTimeframeValue("all_time");
              }}
            />
          ) : noReferrals ? (
            <NoReferrals />
          ) : (
            <>
              {effectiveView === "table" ? (
                <ReferralTableView
                  referrals={filteredReferrals}
                  isLoadingReferrals={isLoadingReferrals}
                  selectedReferral={selectedReferral}
                  onUpdateReferral={handleUpdateReferral}
                  onDeleteReferral={handleDeleteReferral}
                  onRowClick={(referral) => {
                    if (selectedReferral?.id === referral.id) {
                      void navigate("/referrals", { replace: true });
                    } else {
                      void navigate(`/referrals?selected=${referral.id}`, {
                        replace: true,
                      });
                    }
                  }}
                  onPayout={setPayoutReferral}
                />
              ) : (
                <ReferralKanbanView
                  referrals={filteredReferrals}
                  isLoadingReferrals={isLoadingReferrals}
                  selectedReferral={selectedReferral}
                  setUpdateInfoModal={setUpdateInfoModal}
                  updateInfoModal={updateInfoModal}
                  onUpdateReferral={handleUpdateReferral}
                  onDeleteReferral={handleDeleteReferral}
                  onCardClick={(referral) => {
                    posthog?.capture("referral_card_clicked", {
                      referral_id: referral.id,
                      current_status: referral.status,
                      is_deselecting: selected === referral.id,
                    });

                    if (selected === referral.id) {
                      void navigate("/referrals", { replace: true });
                    } else {
                      void navigate(`/referrals?selected=${referral.id}`, {
                        replace: true,
                      });
                    }
                  }}
                  onPayout={setPayoutReferral}
                />
              )}
            </>
          )}
        </PageContainer>
      </SimpleSidebarContent>

      <ReferralsSidebar
        referral={selectedReferral}
        viewType={effectiveView}
        onClose={handleSidebarClose}
        onUpdate={handleSidebarUpdate}
        onNextColumn={(column, referral) => {
          setUpdateInfoModal({
            referral,
            targetColumn: column,
          });
        }}
        onLost={() => {
          if (!selectedReferral) return;
          handleUpdateReferral({ status: "LOST" }, selectedReferral);
        }}
        onPayout={() => {
          if (!selectedReferral) return;
          setPayoutReferral(selectedReferral);
        }}
        onDelete={() => {
          if (!selectedReferral) return;
          handleDeleteReferral(selectedReferral);
        }}
      />

      <AddAdvocateModal
        open={isAddAdvocateModalOpen}
        onOpenChange={setIsAddAdvocateModalOpen}
        onSuccess={() => {
          setIsAddAdvocateModalOpen(false);
        }}
      />
      <AddReferralModal
        open={isAddReferralModalOpen}
        onOpenChange={setIsAddReferralModalOpen}
        onSuccess={(referral) => {
          setIsAddReferralModalOpen(false);
          void navigate(`/referrals?selected=${referral.id}`, {
            replace: true,
          });
        }}
      />
      <PayoutModal
        referral={payoutReferral}
        onOpenChange={setPayoutReferral}
        onFinalPayout={handleFinalPayout}
      />

      <UpdateReferralInfoModal
        open={!!updateInfoModal}
        onOpenChange={(open) =>
          setUpdateInfoModal(open ? updateInfoModal : null)
        }
        referral={updateInfoModal?.referral}
        targetColumn={updateInfoModal?.targetColumn}
        onSubmit={handleUpdateDraggedReferral}
      />
    </SimpleSidebarLayout>
  );
};

export default Referrals;
